import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowUpCircle, ArrowDownCircle, Pencil, Trash2, Search, Eye, FileText, ChevronUp, ChevronDown, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import ProductDetailsView from "@/pages/product/ProductDetailsView";
import ClientDetails from "@/pages/ClientDetails";
import SupplierDetails from "@/pages/SupplierDetails";
import ExpenseCategoryDetails from "@/pages/ExpenseCategoryDetails";
import ProductCategoryDetails from "@/pages/ProductCategoryDetails";
import { formatPhone } from "@/lib/utils";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isEqual,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { NewSaleModal } from "@/components/caixa/NewSaleModal";
import { NewExpenseModal } from "@/components/caixa/NewExpenseModal";
import { PrintPreviewModal } from "@/components/caixa/PrintPreviewModal";
import { TransactionFilters, FilterState } from "@/components/caixa/TransactionFilters";
import CashRegisterSessions from "@/components/caixa/CashRegisterSessions";
import OpenSessionModal from "@/components/caixa/OpenSessionModal";
import CloseSessionModal from "@/components/caixa/CloseSessionModal";
import { useTenant } from "@/contexts/TenantContext";
import { useMoneyVisibility } from "@/contexts/MoneyVisibilityContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TablePagination } from "@/components/ui/table-pagination";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MoneyValue } from "@/components/MoneyValue";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

type Transaction = {
  id: string;
  tipo: "entrada" | "saida";
  valor_total: number;
  valor_liquido: number;
  descricao: string | null;
  status: string;
  created_at: string;
  categoria?: string | null; // Deprecated - usar expense_category_id
  expense_category_id?: string | null;
  client_id: string | null;
  vendedor_id: string | null;
  supplier_id: string | null;
  bank_account_id: string | null;
  payment_method_id: string | null;
  desconto: number;
  data_entrega: string | null;
  pagamento_json: any;
  cash_register_session_id?: string | null;
};

type FacetCounts = {
  tipo: Record<"entrada" | "saida", number>;
  status: Record<string, number>;
  paymentMethods: Record<string, number>;
  clients: Record<string, number>;
  suppliers: Record<string, number>;
  categories: Record<string, number>;
  products: Record<string, number>;
  valueRanges: Record<string, number>;
  bankAccounts: Record<string, number>;
};

const WEEK_OPTIONS = {
  weekStartsOn: 1 as 0 | 1 | 2 | 3 | 4 | 5 | 6,
  locale: ptBR,
};

const withStartAndEndOfDay = (from: Date, to: Date) => ({
  from: startOfDay(from),
  to: endOfDay(to),
});

const QUICK_DATE_PRESETS: Array<{
  value: string;
  label: string;
  range: () => { from: Date; to: Date };
}> = [
  {
    value: "today",
    label: "Hoje",
    range: () => withStartAndEndOfDay(new Date(), new Date()),
  },
  {
    value: "yesterday",
    label: "Ontem",
    range: () => {
      const date = subDays(new Date(), 1);
      return withStartAndEndOfDay(date, date);
    },
  },
  {
    value: "before_yesterday",
    label: "Antes de ontem",
    range: () => {
      const date = subDays(new Date(), 2);
      return withStartAndEndOfDay(date, date);
    },
  },
  {
    value: "this_week",
    label: "Esta semana",
    range: () => {
      const today = new Date();
      const from = startOfWeek(today, WEEK_OPTIONS);
      const to = endOfWeek(today, WEEK_OPTIONS);
      return withStartAndEndOfDay(from, to);
    },
  },
  {
    value: "last_week",
    label: "Semana passada",
    range: () => {
      const lastWeek = subWeeks(new Date(), 1);
      const from = startOfWeek(lastWeek, WEEK_OPTIONS);
      const to = endOfWeek(lastWeek, WEEK_OPTIONS);
      return withStartAndEndOfDay(from, to);
    },
  },
  {
    value: "two_weeks_ago",
    label: "Semana retrasada",
    range: () => {
      const week = subWeeks(new Date(), 2);
      const from = startOfWeek(week, WEEK_OPTIONS);
      const to = endOfWeek(week, WEEK_OPTIONS);
      return withStartAndEndOfDay(from, to);
    },
  },
  {
    value: "this_month",
    label: "Este mês",
    range: () => {
      const today = new Date();
      const from = startOfMonth(today);
      const to = endOfMonth(today);
      return withStartAndEndOfDay(from, to);
    },
  },
  {
    value: "last_month",
    label: "Mês passado",
    range: () => {
      const month = subMonths(new Date(), 1);
      const from = startOfMonth(month);
      const to = endOfMonth(month);
      return withStartAndEndOfDay(from, to);
    },
  },
  {
    value: "two_months_ago",
    label: "Mês retrasado",
    range: () => {
      const month = subMonths(new Date(), 2);
      const from = startOfMonth(month);
      const to = endOfMonth(month);
      return withStartAndEndOfDay(from, to);
    },
  },
  {
    value: "this_year",
    label: "Este ano",
    range: () => {
      const today = new Date();
      const from = startOfYear(today);
      const to = endOfYear(today);
      return withStartAndEndOfDay(from, to);
    },
  },
  {
    value: "last_year",
    label: "Ano passado",
    range: () => {
      const year = subYears(new Date(), 1);
      const from = startOfYear(year);
      const to = endOfYear(year);
      return withStartAndEndOfDay(from, to);
    },
  },
];

const truncateProductName = (name: string | null | undefined): string => {
  if (!name) return "-";
  if (name.length <= 17) return name;
  return name.substring(0, 17) + "...";
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", { 
    style: "currency", 
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

export default function Caixa() {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantId, tenantName } = useTenant();
  const [showNewSale, setShowNewSale] = useState(false);
  const [showNewExpense, setShowNewExpense] = useState(false);
  const [viewOnlyMode, setViewOnlyMode] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showOpenSession, setShowOpenSession] = useState(false);
  const [showCloseSession, setShowCloseSession] = useState(false);
  const [showSummaryTables, setShowSummaryTables] = useState(false);
  const [printPreviewTransaction, setPrintPreviewTransaction] = useState<Transaction | null>(null);
  const [clientProfile, setClientProfile] = useState<{ id: string; name: string } | null>(null);
  const [supplierProfile, setSupplierProfile] = useState<{ id: string; name: string } | null>(null);
  const [productProfile, setProductProfile] = useState<{ id: string; name: string } | null>(null);
  const [expenseCategoryProfile, setExpenseCategoryProfile] = useState<{ id: string; name: string } | null>(null);
  const [productCategoryProfile, setProductCategoryProfile] = useState<{ id: string; name: string } | null>(null);
  const [rxFrom, setRxFrom] = useState<Date | null>(null);
  const [rxTo, setRxTo] = useState<Date | null>(null);
  const productIframeRef = React.useRef<HTMLIFrameElement | null>(null);

  const { data: productHeader } = useQuery({
    queryKey: ["product-header", productProfile?.id],
    enabled: !!productProfile?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, nome, codigo_sku, foto_url, created_at, product_category_id, product_categories(nome)")
        .eq("id", productProfile!.id)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  // Ref para rastrear se o modal foi aberto pelo usuário (não apenas por query param)
  const expenseModalOpenedByUser = React.useRef(false);
  const saleModalOpenedByUser = React.useRef(false);
  
  // Ref para rastrear o último valor de location.search para evitar re-execuções desnecessárias
  const lastSearchRef = React.useRef<string>(location.search);
  
  // Ref para evitar que o useEffect interfira quando o modal está sendo fechado programaticamente
  const isClosingModalRef = React.useRef(false);

  // Verificar se deve abrir o modal de despesa via query parameter
  // Só sincroniza quando há mudança real nos query params, não quando a aba perde/ganha foco
  useEffect(() => {
    // Ignorar se estamos fechando o modal programaticamente
    if (isClosingModalRef.current) {
      isClosingModalRef.current = false;
      return;
    }
    
    const currentSearch = location.search;
    
    // Se não mudou, não fazer nada (evita re-execução ao trocar de aba)
    if (currentSearch === lastSearchRef.current) {
      return;
    }
    
    // Atualizar ref com o valor atual
    lastSearchRef.current = currentSearch;
    
    const searchParams = new URLSearchParams(currentSearch);
    const hasOpenExpenseParam = searchParams.get('openExpense') === 'true';
    
    if (hasOpenExpenseParam && !showNewExpense) {
      // Query param indica que deve abrir o modal
      setShowNewExpense(true);
      expenseModalOpenedByUser.current = false;
    } else if (!hasOpenExpenseParam && showNewExpense && !expenseModalOpenedByUser.current) {
      // Query param foi removido e modal foi aberto via URL, então fechar
      setShowNewExpense(false);
    }
    // Se modal está aberto e user abriu manualmente (não via URL), não fechar mesmo sem query param
  }, [location.search, showNewExpense]); // Adicionado showNewExpense para evitar fechamento quando o modal já está aberto

  // Handler compartilhado para modais de Nova Venda/Despesa
  const handleCloseModal = (open: boolean, modalType?: 'sale' | 'expense') => {
    if (!open) {
      // Marcar que estamos fechando programaticamente para evitar que o useEffect interfira
      if (modalType === 'expense') {
        isClosingModalRef.current = true;
        expenseModalOpenedByUser.current = false;
        setShowNewExpense(false);
        // Remover parâmetro openExpense da URL quando fechar o modal
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.has('openExpense')) {
          searchParams.delete('openExpense');
          const newSearch = searchParams.toString();
          // Atualizar ref antes de navegar para evitar que o useEffect reaja
          lastSearchRef.current = newSearch ? `?${newSearch}` : '';
          navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
        }
      } else {
        saleModalOpenedByUser.current = false;
        setShowNewSale(false);
      }
      setEditingTransaction(null);
      setViewOnlyMode(false);
    } else {
      // Se o modal está abrindo, marcar que foi aberto pelo usuário
      if (modalType === 'expense') {
        expenseModalOpenedByUser.current = true;
        setShowNewExpense(true);
        // Adicionar parâmetro à URL apenas se não estiver presente
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get('openExpense') !== 'true') {
          searchParams.set('openExpense', 'true');
          const newSearch = `?${searchParams.toString()}`;
          // Atualizar ref antes de navegar para evitar que o useEffect reaja
          lastSearchRef.current = newSearch;
          navigate(`${location.pathname}${newSearch}`, { replace: true });
        }
      } else {
        saleModalOpenedByUser.current = true;
        setShowNewSale(true);
      }
    }
  };

  const { showMoneyValues, toggleShowMoneyValues } = useMoneyVisibility();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Inicializar com o período "Hoje" por padrão
  const [filters, setFilters] = useState<FilterState>(() => {
    const today = new Date();
    return {
      searchTerm: "",
      status: [],
      paymentMethods: [],
      clients: [],
      suppliers: [],
      categories: [],
      bankAccounts: [],
      products: [],
      valueRange: "",
      dateFrom: startOfDay(today),
      dateTo: endOfDay(today),
      deliveryDateFrom: undefined,
      deliveryDateTo: undefined,
      tipo: [],
    };
  });

  // Sessões de caixa fechadas (janelas de tempo por conta)
  const { data: closedSessionWindows = [] } = useQuery({
    queryKey: ["closed-session-windows", tenantId],
    queryFn: async () => {
      if (!tenantId) return [] as Array<{ id: string; bank_account_id: string; opened_at: string; closed_at: string }>; 
      const { data, error } = await supabase
        .from("cash_register_sessions")
        .select("id, bank_account_id, opened_at, closed_at")
        .eq("tenant_id", tenantId)
        .eq("status", "closed")
        .order("closed_at", { ascending: false });
      if (error) throw error;
      return (data || []) as Array<{ id: string; bank_account_id: string; opened_at: string; closed_at: string }>;
    },
    enabled: !!tenantId,
  });

  // Índice por conta com janelas [opened_at, closed_at] e janelas globais (sem conta)
  const closedWindowsByAccount = useMemo<Record<string, Array<{ from: Date; to: Date }>>>(() => {
    const map: Record<string, Array<{ from: Date; to: Date }>> = {};
    (closedSessionWindows || []).forEach((s) => {
      if (!s.opened_at || !s.closed_at) return;
      const key = s.bank_account_id || "*"; // '*' representa janela global (todas as contas)
      if (!map[key]) map[key] = [];
      map[key].push({ from: new Date(s.opened_at), to: new Date(s.closed_at) });
    });
    return map;
  }, [closedSessionWindows]);

  // Conjunto de IDs de sessões fechadas para checar vínculo direto
  const closedSessionIdSet = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    (closedSessionWindows || []).forEach((s:any) => { if (s?.id) set.add(s.id); });
    return set;
  }, [closedSessionWindows]);

  const isLockedByClosedSession = (tx: Transaction): boolean => {
    // 1) Se a transação está explicitamente vinculada a uma sessão fechada, bloqueia
    if (tx.cash_register_session_id && closedSessionIdSet.has(tx.cash_register_session_id)) {
      return true;
    }

    const inWindow = (accountId?: string | null, dateIso?: string | null) => {
      if (!dateIso) return false;
      const windows = [
        ...(accountId ? (closedWindowsByAccount[accountId] || []) : []),
        ...(closedWindowsByAccount["*"] || []),
      ];
      if (windows.length === 0) return false;
      const d = new Date(dateIso);
      return windows.some((w) => d >= w.from && d <= w.to);
    };

    // Entradas: podem não ter bank_account_id em transactions; usar transaction_payments
    if (tx.tipo === "entrada") {
      const pays = paymentsByTx[tx.id] || [];
      if (pays.length === 0) return false;
      return pays.some((p) => inWindow(p.bank_account_id, p.created_at || p.confirmed_at || tx.created_at));
    }

    // Saídas: usam bank_account_id diretamente na transação
    return inWindow(tx.bank_account_id, tx.created_at);
  };

  // Atualiza imediatamente o estado do botão Abrir/Fechar Caixa e listas ao fechar os modais
  useEffect(() => {
    if (!tenantId) return;
    // Quando os dois estiverem fechados (ou qualquer um fechando), refaz a busca
    if (!showOpenSession && !showCloseSession) {
      queryClient.invalidateQueries({ queryKey: ["open-session-shortcut", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["open-sessions", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["closed-sessions", tenantId] });
      // Atualiza também o FAB
      queryClient.invalidateQueries({ queryKey: ["fab-open-session", tenantId] });
    }
  }, [showOpenSession, showCloseSession, tenantId]);

  // Sessão aberta (atalho para abrir/fechar caixa da aba Transações)
  const { data: openSessionShortcut } = useQuery({
    queryKey: ["open-session-shortcut", tenantId],
    queryFn: async () => {
      if (!tenantId) return null as any;
      const { data, error } = await supabase
        .from("cash_register_sessions")
        .select("* , bank_accounts(id,nome,icone)")
        .eq("tenant_id", tenantId)
        .eq("status", "open")
        .order("opened_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      return (data && data.length > 0) ? data[0] : null;
    },
    enabled: !!tenantId,
  });

  // Buscar categorias de despesa para criar mapa nome -> id
  const { data: expenseCategories = [] } = useQuery({
    queryKey: ["expense-categories", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("expense_categories")
        .select("id, nome")
        .eq("tenant_id", tenantId)
        .order("nome");
      if (error) throw error;
      return data || [];
    },
  });

  // Mapa expense_category_id -> nome
  const expenseCategoryNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    expenseCategories.forEach((cat: any) => {
      if (cat?.id && cat?.nome) {
        map[cat.id] = cat.nome;
      }
    });
    return map;
  }, [expenseCategories]);

  // Buscar categorias de produtos
  const { data: productCategories = [] } = useQuery({
    queryKey: ["product-categories", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("product_categories")
        .select("id, nome")
        .eq("tenant_id", tenantId)
        .eq("status", "ativo")
        .order("nome");
      if (error) throw error;
      return data || [];
    },
  });

  // Mapa product_category_id -> nome
  const productCategoryNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    productCategories.forEach((cat: any) => {
      if (cat?.id && cat?.nome) {
        map[cat.id] = cat.nome;
      }
    });
    return map;
  }, [productCategories]);

  // Mapa nome -> id para buscar rapidamente o ID pelo nome
  const categoryNameToIdMap = useMemo(() => {
    const map = new Map<string, string>();
    expenseCategories.forEach((cat: any) => {
      if (cat.nome && cat.id) {
        map.set(cat.nome, cat.id);
      }
    });
    return map;
  }, [expenseCategories]);

  const { data: clients = [] } = useQuery({
    queryKey: ["clients", tenantId],
    queryFn: async () => {
      if (!tenantId) return [] as any[];
      const { data, error } = await supabase
        .from("clients")
        .select("id, nome, telefone, email")
        .eq("tenant_id", tenantId)
        .order("nome");
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers", tenantId],
    queryFn: async () => {
      if (!tenantId) return [] as any[];
      const { data, error } = await supabase
        .from("suppliers")
        .select("id, nome, telefone")
        .eq("tenant_id", tenantId)
        .order("nome");
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
  });

  const { data: sellers = [] } = useQuery({
    queryKey: ["sellers", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("tenant_id", tenantId)
        .order("nome_completo");
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
  });

  const { data: bankAccountsData = [] } = useQuery({
    queryKey: ["bank-accounts", tenantId],
    queryFn: async () => {
      if (!tenantId) return [] as Array<{ id: string; nome: string }>; 
      const { data, error } = await supabase
        .from("bank_accounts")
        .select("id, nome, status, tenant_id")
        .eq("tenant_id", tenantId)
        .eq("status", "ativo")
        .order("nome");
      if (error) throw error;
      return (data || []).map((a: any) => ({ id: a.id, nome: a.nome })) as Array<{ id: string; nome: string }>;
    },
    enabled: !!tenantId,
  });

  const bankAccountsRef = bankAccountsData;

  const handleFiltersChange = (newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const quickDateValue = useMemo<string | undefined>(() => {
    if (!filters.dateFrom || !filters.dateTo) {
      return undefined;
    }

    const normalizedFrom = startOfDay(filters.dateFrom);
    const normalizedTo = endOfDay(filters.dateTo);

    const preset = QUICK_DATE_PRESETS.find(({ range }) => {
      const { from, to } = range();
      return isEqual(from, normalizedFrom) && isEqual(to, normalizedTo);
    });

    return preset?.value;
  }, [filters.dateFrom, filters.dateTo]);

  const handleQuickDateChange = (value: string) => {
    if (value === "clear") {
      handleFiltersChange({
        ...filters,
        dateFrom: undefined,
        dateTo: undefined,
      });
      return;
    }

    const preset = QUICK_DATE_PRESETS.find((item) => item.value === value);
    if (!preset) return;

    const { from, to } = preset.range();
    handleFiltersChange({
      ...filters,
      dateFrom: from,
      dateTo: to,
    });
  };

  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("transactions")
        .delete()
        .eq("id", id);
      if (error) throw error;
      return id;
    },
    onSuccess: (deletedId) => {
      queryClient.invalidateQueries({ queryKey: ["caixa-transactions"] });
      setDeletingId(null);
      toast.success("Transação excluída com sucesso!");
    },
  });

  const { data: transactions, isLoading } = useQuery({
    queryKey: ["caixa-transactions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  // Carregar IDs das transações para buscar itens e pagamentos
  const transactionIds = useMemo(() => (transactions || []).map(t => t.id), [transactions]);

  // Buscar itens das transações
  const { data: transactionItems = [] } = useQuery({
    queryKey: ["transaction-items", transactionIds],
    queryFn: async () => {
      if (!transactionIds || transactionIds.length === 0) return [];
      const { data, error } = await supabase
        .from("transaction_items")
        .select("*, products(nome, unit_measurement_sale_id, product_category_id)")
        .in("transaction_id", transactionIds);
      if (error) throw error;
      return data || [];
    },
    enabled: transactionIds.length > 0,
  });

  // Buscar unidades de medida
  const { data: unitMeasurements = [] } = useQuery({
    queryKey: ["unit_measurements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("unit_measurements" as any)
        .select("*")
        .order("nome", { ascending: true });
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  type TxPayment = {
    transaction_id: string;
    bank_account_id: string | null;
    payment_method_id: string | null;
    valor: number;
    net_amount?: number | null;
    status: string;
    data?: string | null;
    data_prevista?: string | null;
    created_at?: string | null;
    confirmed_at?: string | null;
  };

  const { data: txPayments = [] } = useQuery({
    queryKey: ["transaction-payments", transactionIds],
    queryFn: async () => {
      if (!transactionIds || transactionIds.length === 0) return [] as TxPayment[];
      const { data, error } = await (supabase as any)
        .from("transaction_payments")
        .select("transaction_id, bank_account_id, payment_method_id, valor, net_amount, status, data, data_prevista, created_at, confirmed_at")
        .in("transaction_id", transactionIds);
      if (error) throw error;
      return (data || []).map((r: any) => ({
        transaction_id: r.transaction_id,
        bank_account_id: r.bank_account_id,
        payment_method_id: r.payment_method_id,
        valor: Number(r.valor) || 0,
        net_amount: r.net_amount != null ? Number(r.net_amount) : null,
        status: r.status,
        data: r.data || null,
        data_prevista: r.data_prevista || null,
        created_at: r.created_at || null,
        confirmed_at: r.confirmed_at || null,
      })) as TxPayment[];
    },
    enabled: transactionIds.length > 0,
  });

  const paymentsByTx = useMemo<Record<string, TxPayment[]>>(() => {
    const map: Record<string, TxPayment[]> = {};
    (txPayments || []).forEach((p) => {
      if (!map[p.transaction_id]) map[p.transaction_id] = [];
      map[p.transaction_id].push(p);
    });
    return map;
  }, [txPayments]);

  // ... (rest of the code remains the same)

  const renderTransactionValue = (transaction: Transaction, align: "center" | "start" | "end" = "center") => {
    // Usar transaction_payments em vez de pagamento_json
    const pagamentos = transaction.tipo === "entrada" 
      ? (paymentsByTx[transaction.id] || [])
      : [];
    const unicoCobrindoTotal = transaction.tipo === "entrada"
      && pagamentos.length === 1
      && Number(pagamentos[0]?.valor || 0) === Number(transaction.valor_liquido || 0);
    const unicoPendente = unicoCobrindoTotal && pagamentos[0]?.status === "pendente";
    const amountClass = transaction.tipo === "entrada"
      ? (unicoPendente ? "text-warning font-semibold" : "text-success font-semibold")
      : "text-destructive font-semibold";
    const justifyClass = align === "center" ? "justify-center" : align === "end" ? "justify-end" : "justify-start";
    const containerClass = align === "center" ? "items-center text-center" : align === "end" ? "items-end text-right" : "items-start text-left";
    
    // Helper para renderizar conta e método
    const renderAccountMethod = (p: any) => {
      const contaNome = (bankAccountNameById && p.bank_account_id) ? bankAccountNameById[String(p.bank_account_id)] : undefined;
      const metodoNome = (paymentMethodNameById && p.payment_method_id) ? paymentMethodNameById[String(p.payment_method_id)] : undefined;
      if (!contaNome && !metodoNome) return null;
      return (
        <span className="text-[10px] md:text-xs text-muted-foreground ml-2">
          {contaNome}{contaNome && metodoNome ? " • " : ""}{metodoNome}
        </span>
      );
    };
    
    const primaryDetails = transaction.tipo === "saida"
      ? renderAccountMethod(transaction)
      : (unicoCobrindoTotal ? renderAccountMethod(pagamentos[0]) : null);

    const liquidoRecebido = pagamentos.reduce((sum, p) => {
      const net = typeof p.net_amount === "number" ? Number(p.net_amount) : Number(p.valor) || 0;
      return sum + net;
    }, 0);
    const displayLiquido = transaction.tipo === "entrada"
      ? (pagamentos.length > 0 ? liquidoRecebido : Number(transaction.valor_liquido || 0))
      : null;
    const valorBruto = Number(transaction.valor_liquido || 0);
    const shouldShowLiquido = transaction.tipo === "entrada" 
      && displayLiquido !== null 
      && Math.abs(displayLiquido - valorBruto) > 0.01; // Comparação com tolerância para valores decimais

    const valorPrincipal = (
      <div className="flex flex-col gap-1">
        <div className={`flex items-center gap-1 ${justifyClass}`}>
          <MoneyValue
            value={Number(transaction.valor_liquido)}
            prefix={transaction.tipo === "entrada" ? "+" : "-"}
            valueClassName={amountClass}
            className={justifyClass}
          />
          {primaryDetails}
        </div>
        {shouldShowLiquido && (
          <div className={`flex items-center gap-1 text-[11px] md:text-xs text-muted-foreground ${justifyClass}`}>
            <span>Recebido líquido:</span>
            <MoneyValue
              asBlock
              value={Number(displayLiquido || 0)}
              prefix={transaction.tipo === "entrada" ? "+" : ""}
              valueClassName="font-medium text-primary"
              className={justifyClass}
            />
          </div>
        )}
      </div>
    );

    const renderTooltipInfo = (p: any) => {
      const tooltipText = p.status === "confirmado"
        ? (p.data ? `Pago em: ${format(new Date(p.data), "dd/MM/yyyy", { locale: ptBR })}` : "Pago")
        : (p.data_prevista ? `Previsto: ${format(new Date(p.data_prevista), "dd/MM/yyyy", { locale: ptBR })}` : "Previsto: -");
      const contaNome = (bankAccountNameById && p.bank_account_id) ? bankAccountNameById[String(p.bank_account_id)] : undefined;
      const metodoNome = (paymentMethodNameById && p.payment_method_id) ? paymentMethodNameById[String(p.payment_method_id)] : undefined;
      const valorRecebido = typeof p.net_amount === "number" ? Number(p.net_amount) : Number(p.valor) || 0;
      const valorRecebidoClass = p.status === "confirmado" ? "font-semibold text-success" : "font-semibold text-warning";
      return (
        <div className="text-xs space-y-1">
          {(contaNome || metodoNome) && (
            <div className="font-medium">{contaNome}{contaNome && metodoNome ? " - " : ""}{metodoNome}</div>
          )}
          <div className="text-muted-foreground">{tooltipText}</div>
          <div className="flex items-center gap-1">
            <span className="font-medium">Valor Recebido:</span>
            <MoneyValue
              asBlock
              value={valorRecebido}
              prefix="+"
              valueClassName={valorRecebidoClass}
              className="justify-start"
            />
          </div>
        </div>
      );
    };

    return (
      <div className={`flex flex-col gap-1 ${containerClass}`}>
        {unicoCobrindoTotal ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                {valorPrincipal}
              </TooltipTrigger>
              <TooltipContent>
                {renderTooltipInfo(pagamentos[0])}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          valorPrincipal
        )}

        {transaction.tipo === "entrada" && pagamentos.length > 0 && !unicoCobrindoTotal && (
          <TooltipProvider>
            <div className="space-y-0.5">
              {pagamentos.map((p: any, idx: number) => {
                const colorClass = p.status === "confirmado" ? "text-success" : "text-warning";
                return (
                  <Tooltip key={idx}>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1">
                        <MoneyValue
                          asBlock
                          value={Number(p.valor || 0)}
                          valueClassName={`text-[11px] md:text-xs leading-tight ${colorClass}`}
                          className={justifyClass}
                        />
                        {renderAccountMethod(p)}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {renderTooltipInfo(p)}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>
        )}
      </div>
    );
  };

  const renderTypeBadge = (transaction: Transaction) => {
    return (
      <Badge
        variant={transaction.tipo === "entrada" ? "default" : "secondary"}
        className={
          transaction.tipo === "entrada"
            ? "bg-success/10 text-success border border-success"
            : "bg-destructive/10 text-destructive border border-destructive"
        }
      >
        {transaction.tipo === "entrada" ? (
          <ArrowUpCircle className="h-3 w-3 mr-1" />
        ) : (
          <ArrowDownCircle className="h-3 w-3 mr-1" />
        )}
        {transaction.tipo === "entrada" ? "Entrada" : "Saída"}
      </Badge>
    );
  };

  const renderStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: "default" | "secondary" | "outline"; className: string }> = {
      paga: {
        variant: "default",
        className: "border border-success bg-success/15 text-success hover:bg-success hover:text-white",
      },
      pendente: {
        variant: "secondary",
        className: "border border-warning bg-warning/15 text-warning hover:bg-warning hover:text-white",
      },
      confirmado: {
        variant: "default",
        className: "border border-success bg-success/15 text-success hover:bg-success hover:text-white",
      },
    };

    const config = statusConfig[status] || {
      variant: "outline" as const,
      className: "",
    };

    return (
      <Badge variant={config.variant} className={config.className}>
        {status}
      </Badge>
    );
  };

  const handleEdit = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    if (transaction.tipo === "entrada") {
      saleModalOpenedByUser.current = true;
      setShowNewSale(true);
    } else {
      expenseModalOpenedByUser.current = true;
      setShowNewExpense(true);
      // Atualizar URL com parâmetro openExpense apenas se não estiver presente
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get('openExpense') !== 'true') {
        searchParams.set('openExpense', 'true');
        const newSearch = `?${searchParams.toString()}`;
        // Atualizar ref antes de navegar para evitar que o useEffect reaja
        lastSearchRef.current = newSearch;
        navigate(`${location.pathname}${newSearch}`, { replace: true });
      }
    }
  };

  const quickLabelByValue = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    QUICK_DATE_PRESETS.forEach(p => { map[p.value] = p.label; });
    return map;
  }, []);

  const clientNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (clients || []).forEach((c: any) => { map[c.id] = c.nome || c.id; });
    return map;
  }, [clients]);

  const supplierNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (suppliers || []).forEach((s: any) => { map[s.id] = s.nome || s.id; });
    return map;
  }, [suppliers]);

  const bankAccountNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (bankAccountsRef || []).forEach((a: any) => { map[a.id] = a.nome || a.id; });
    return map;
  }, [bankAccountsRef]);

  const allPaymentMethods = useMemo(() => {
    const methods = new Set<string>();
    transactions?.forEach((t) => {
      if (t.tipo === "entrada") {
        (paymentsByTx[t.id] || []).forEach((p) => {
          if (p.payment_method_id) methods.add(String(p.payment_method_id));
        });
      } else if (t.tipo === "saida") {
        const id = t.payment_method_id || t.pagamento_json?.metodo_pagamento;
        if (id) methods.add(String(id));
      }
    });
    return Array.from(methods).sort();
  }, [transactions, paymentsByTx]);

  // Buscar nomes dos métodos de pagamento usados no período (evita undefined)
  const { data: paymentMethodsRows = [] } = useQuery({
    queryKey: ["payment-method-names", allPaymentMethods],
    queryFn: async () => {
      if (!allPaymentMethods || allPaymentMethods.length === 0) return [] as Array<{ id: string; nome: string }>;
      const { data, error } = await supabase
        .from("payment_methods")
        .select("id, nome")
        .in("id", allPaymentMethods);
      if (error) throw error;
      return (data || []) as Array<{ id: string; nome: string }>;
    },
    enabled: allPaymentMethods.length > 0,
  });

  const paymentMethodNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (paymentMethodsRows || []).forEach((r: any) => {
      if (r?.id) map[String(r.id)] = r?.nome || String(r.id);
    });
    return map;
  }, [paymentMethodsRows]);

  const allProducts = useMemo(() => {
    const productIds = new Set<string>();
    (transactionItems || []).forEach((item: any) => {
      if (item.product_id) productIds.add(String(item.product_id));
    });
    return Array.from(productIds).sort();
  }, [transactionItems]);

  // Buscar nomes dos produtos usados no período (evita undefined)
  const { data: productsRows = [] } = useQuery({
    queryKey: ["product-names", allProducts],
    queryFn: async () => {
      if (!allProducts || allProducts.length === 0) return [] as Array<{ id: string; nome: string }>;
      const { data, error } = await supabase
        .from("products")
        .select("id, nome")
        .in("id", allProducts);
      if (error) throw error;
      return (data || []) as Array<{ id: string; nome: string }>;
    },
    enabled: allProducts.length > 0,
  });

  const productNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (productsRows || []).forEach((r: any) => {
      if (r?.id) map[String(r.id)] = r?.nome || String(r.id);
    });
    return map;
  }, [productsRows]);

  // Função para normalizar texto removendo acentos
  const normalizeText = (text: string | null | undefined): string => {
    if (!text) return "";
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, ""); // Remove acentos
  };

  const matchesFilters = (transaction: Transaction, except?: keyof FilterState) => {
    if (filters.searchTerm && except !== "searchTerm") {
      const searchNormalized = normalizeText(filters.searchTerm);
      
      // Buscar em descrição
      let matchesSearch = normalizeText(transaction.descricao).includes(searchNormalized);
      
      // Buscar em nome do cliente
      if (!matchesSearch && transaction.client_id) {
        const clientName = clientNameById[transaction.client_id] || "";
        if (normalizeText(clientName).includes(searchNormalized)) {
          matchesSearch = true;
        }
      }
      
      // Buscar em nome do fornecedor
      if (!matchesSearch && transaction.supplier_id) {
        const supplierName = supplierNameById[transaction.supplier_id] || "";
        if (normalizeText(supplierName).includes(searchNormalized)) {
          matchesSearch = true;
        }
      }
      
      // Buscar em nome dos produtos (apenas para entradas)
      if (!matchesSearch && transaction.tipo === "entrada") {
        const items = transactionItems.filter((item: any) => item.transaction_id === transaction.id);
        matchesSearch = items.some((item: any) => {
          if (item.product_id) {
            const productName = productNameById[String(item.product_id)] || "";
            if (normalizeText(productName).includes(searchNormalized)) {
              return true;
            }
            // Buscar em nome da categoria do produto
            const product = item.products;
            if (product?.product_category_id) {
              const categoryName = productCategoryNameById[product.product_category_id] || "";
              if (normalizeText(categoryName).includes(searchNormalized)) {
                return true;
              }
            }
          }
          return false;
        });
      }
      
      // Buscar em nome da categoria de despesa (apenas para saídas)
      if (!matchesSearch && transaction.tipo === "saida") {
        const expenseCategoryId = (transaction as any).expense_category_id;
        if (expenseCategoryId) {
          const categoryName = expenseCategoryNameById[expenseCategoryId] || "";
          if (normalizeText(categoryName).includes(searchNormalized)) {
            matchesSearch = true;
          }
        }
      }
      
      if (!matchesSearch) return false;
    }

    if (filters.tipo.length > 0 && except !== "tipo") {
      if (!filters.tipo.includes(transaction.tipo)) return false;
    }

    if (filters.status.length > 0 && except !== "status") {
      if (!filters.status.includes(transaction.status)) return false;
    }

    if (filters.paymentMethods.length > 0 && except !== "paymentMethods") {
      if (transaction.tipo === "entrada") {
        const pays = paymentsByTx[transaction.id] || [];
        const hasMethods = pays.some((p) =>
          p.payment_method_id && filters.paymentMethods.includes(String(p.payment_method_id))
        );
        if (!hasMethods) return false;
      } else {
        const method = transaction.payment_method_id || transaction.pagamento_json?.metodo_pagamento;
        if (!method || !filters.paymentMethods.includes(String(method))) return false;
      }
    }

    if (filters.clients.length > 0 && except !== "clients") {
      if (!transaction.client_id || !filters.clients.includes(transaction.client_id)) return false;
    }

    if (filters.suppliers.length > 0 && except !== "suppliers") {
      if (!transaction.supplier_id || !filters.suppliers.includes(transaction.supplier_id)) return false;
    }

    if (filters.categories.length > 0 && except !== "categories") {
      // Para transações de saída, verificar expense_category_id
      if (transaction.tipo === "saida") {
        const expenseCategoryId = (transaction as any).expense_category_id;
        if (!expenseCategoryId || !filters.categories.includes(expenseCategoryId)) return false;
      } else {
        // Entradas não têm expense_category_id
        return false;
      }
    }

    if (filters.bankAccounts.length > 0 && except !== "bankAccounts") {
      if (transaction.tipo === "entrada") {
        const pays = paymentsByTx[transaction.id] || [];
        const hasAccount = pays.some((p) =>
          p.bank_account_id && filters.bankAccounts.includes(String(p.bank_account_id))
        );
        if (!hasAccount) return false;
      } else {
        if (!transaction.bank_account_id || !filters.bankAccounts.includes(transaction.bank_account_id)) return false;
      }
    }

    if (filters.products.length > 0 && except !== "products") {
      // Para transações de entrada, verificar se algum item contém os produtos filtrados
      if (transaction.tipo === "entrada") {
        const items = transactionItems.filter((item: any) => item.transaction_id === transaction.id);
        const hasProduct = items.some((item: any) =>
          item.product_id && filters.products.includes(String(item.product_id))
        );
        if (!hasProduct) return false;
      }
      // Saídas não têm produtos associados
    }

    if (filters.valueRange && except !== "valueRange") {
      const value = Number(transaction.valor_liquido);
      const [min, max] = filters.valueRange.includes("+")
        ? [5000, Infinity]
        : filters.valueRange.split("-").map(Number);
      if (value < min || value > max) return false;
    }

    if ((filters.dateFrom || filters.dateTo) && except !== "dateFrom" && except !== "dateTo") {
      const transactionDate = new Date(transaction.created_at);
      const from = filters.dateFrom ? startOfDay(filters.dateFrom) : undefined;
      const to = filters.dateTo ? endOfDay(filters.dateTo) : undefined;
      if (from && transactionDate < from) return false;
      if (to && transactionDate > to) return false;
    }

    if ((filters.deliveryDateFrom || filters.deliveryDateTo) && except !== "deliveryDateFrom" && except !== "deliveryDateTo") {
      const deliveryDate = transaction.data_entrega
        ? new Date(transaction.data_entrega)
        : transaction.pagamento_json?.data_vencimento
        ? new Date(transaction.pagamento_json.data_vencimento)
        : null;

      if (!deliveryDate) return false;
      const fromDelivery = filters.deliveryDateFrom ? startOfDay(filters.deliveryDateFrom) : undefined;
      const toDelivery = filters.deliveryDateTo ? endOfDay(filters.deliveryDateTo) : undefined;
      if (fromDelivery && deliveryDate < fromDelivery) return false;
      if (toDelivery && deliveryDate > toDelivery) return false;
    }

    return true;
  };

  const facetCounts: FacetCounts = useMemo(() => {
    const counts: FacetCounts = {
      tipo: { entrada: 0, saida: 0 },
      status: {},
      paymentMethods: {},
      clients: {},
      suppliers: {},
      categories: {},
      products: {},
      valueRanges: {},
      bankAccounts: {},
    };
    if (!transactions) return counts;

    transactions.forEach((t) => {
      if (matchesFilters(t, "tipo")) {
        counts.tipo[t.tipo] = (counts.tipo[t.tipo] || 0) + 1;
      }

      if (t.status && matchesFilters(t, "status")) {
        counts.status[t.status] = (counts.status[t.status] || 0) + 1;
      }

      if (matchesFilters(t, "paymentMethods")) {
        if (t.tipo === "entrada") {
          const set = new Set<string>();
          (paymentsByTx[t.id] || []).forEach((p) => {
            if (p.payment_method_id) set.add(String(p.payment_method_id));
          });
          set.forEach((m) => {
            counts.paymentMethods[m] = (counts.paymentMethods[m] || 0) + 1;
          });
        } else {
          const m = t.payment_method_id || t.pagamento_json?.metodo_pagamento;
          if (m) counts.paymentMethods[String(m)] = (counts.paymentMethods[String(m)] || 0) + 1;
        }
      }

      if (t.client_id && matchesFilters(t, "clients")) {
        counts.clients[t.client_id] = (counts.clients[t.client_id] || 0) + 1;
      }

      if (t.supplier_id && matchesFilters(t, "suppliers")) {
        counts.suppliers[t.supplier_id] = (counts.suppliers[t.supplier_id] || 0) + 1;
      }

      if (matchesFilters(t, "categories")) {
        // Para transações de saída, usar expense_category_id
        if (t.tipo === "saida") {
          const expenseCategoryId = (t as any).expense_category_id;
          if (expenseCategoryId) {
            counts.categories[expenseCategoryId] = (counts.categories[expenseCategoryId] || 0) + 1;
          }
        }
      }

      if (matchesFilters(t, "bankAccounts")) {
        if (t.tipo === "entrada") {
          const set = new Set<string>();
          (paymentsByTx[t.id] || []).forEach((p) => {
            if (p.bank_account_id) set.add(String(p.bank_account_id));
          });
          set.forEach((accountId) => {
            counts.bankAccounts[accountId] = (counts.bankAccounts[accountId] || 0) + 1;
          });
        } else {
          if (t.bank_account_id) {
            counts.bankAccounts[t.bank_account_id] = (counts.bankAccounts[t.bank_account_id] || 0) + 1;
          }
        }
      }

      if (matchesFilters(t, "products")) {
        // Apenas transações de entrada têm produtos
        if (t.tipo === "entrada") {
          const set = new Set<string>();
          (transactionItems || []).forEach((item: any) => {
            if (item.transaction_id === t.id && item.product_id) {
              set.add(String(item.product_id));
            }
          });
          set.forEach((productId) => {
            counts.products[productId] = (counts.products[productId] || 0) + 1;
          });
        }
      }

      // Value ranges (ignore only valueRange filter)
      if (matchesFilters(t, "valueRange")) {
        const value = Number(t.valor_liquido);
        const bucket =
          value <= 100 ? "0-100" :
          value <= 500 ? "100-500" :
          value <= 1000 ? "500-1000" :
          value <= 5000 ? "1000-5000" :
          "5000+";
        counts.valueRanges[bucket] = (counts.valueRanges[bucket] || 0) + 1;
      }
    });

    return counts;
  }, [transactions, filters, paymentsByTx, transactionItems]);

  const activeFilterChips = useMemo<{ id: string; label: React.ReactNode; onClear: () => void }[]>(() => {
    const chips: { id: string; label: React.ReactNode; onClear: () => void }[] = [];
    if (filters.searchTerm) {
      const term = filters.searchTerm;
      chips.push({
        id: `search:${term}`,
        label: `Busca: "${term}"`,
        onClear: () => handleFiltersChange({ ...filters, searchTerm: "" }),
      });
    }
    filters.tipo.forEach((t) => {
      const label = t === "entrada" ? "Entrada" : "Saída";
      chips.push({
        id: `tipo:${t}`,
        label: `Tipo: ${label}`,
        onClear: () => handleFiltersChange({ ...filters, tipo: filters.tipo.filter((x) => x !== t) }),
      });
    });
    filters.status.forEach((s) => {
      chips.push({
        id: `status:${s}`,
        label: `Status: ${s}`,
        onClear: () => handleFiltersChange({ ...filters, status: filters.status.filter((x) => x !== s) }),
      });
    });
    filters.paymentMethods.forEach((m) => {
      const name = paymentMethodNameById[m] || m;
      chips.push({
        id: `method:${m}`,
        label: `Método: ${name}`,
        onClear: () => handleFiltersChange({ ...filters, paymentMethods: filters.paymentMethods.filter((x) => x !== m) }),
      });
    });
    filters.clients.forEach((id) => {
      const name = clientNameById[id] || id;
      chips.push({
        id: `client:${id}`,
        label: `Cliente: ${name}`,
        onClear: () => handleFiltersChange({ ...filters, clients: filters.clients.filter((x) => x !== id) }),
      });
    });
    filters.suppliers.forEach((id) => {
      const name = supplierNameById[id] || id;
      chips.push({
        id: `supplier:${id}`,
        label: `Fornecedor: ${name}`,
        onClear: () => handleFiltersChange({ ...filters, suppliers: filters.suppliers.filter((x) => x !== id) }),
      });
    });
    filters.products.forEach((productId) => {
      const name = productNameById[productId] || productId;
      chips.push({
        id: `product:${productId}`,
        label: `Produto: ${name}`,
        onClear: () => handleFiltersChange({ ...filters, products: filters.products.filter((x) => x !== productId) }),
      });
    });
    filters.categories.forEach((c) => {
      chips.push({
        id: `category:${c}`,
        label: `Categoria: ${c}`,
        onClear: () => handleFiltersChange({ ...filters, categories: filters.categories.filter((x) => x !== c) }),
      });
    });

    filters.bankAccounts.forEach((id) => {
      const name = bankAccountNameById[id] || id;
      chips.push({
        id: `bank:${id}`,
        label: `Conta: ${name}`,
        onClear: () => handleFiltersChange({ ...filters, bankAccounts: filters.bankAccounts.filter((x) => x !== id) }),
      });
    });
    if (filters.valueRange) {
      const vr = filters.valueRange;
      const formatValue = (value: number) =>
        new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

      const maskValue = (formatted: string) =>
        showMoneyValues ? formatted : formatted.replace(/[0-9]/g, "*");

      let labelRange = vr;
      if (vr.includes("+")) {
        const base = Number(vr.replace("+", "")) || 0;
        labelRange = `${maskValue(formatValue(base))}+`;
      } else if (vr.includes("-")) {
        const [a, b] = vr.split("-").map((x) => Number(x) || 0);
        labelRange = `${maskValue(formatValue(a))} - ${maskValue(formatValue(b))}`;
      } else {
        const value = Number(vr) || 0;
        labelRange = maskValue(formatValue(value));
      }

      chips.push({
        id: `values:${vr}`,
        label: `Valores: ${labelRange}`,
        onClear: () => handleFiltersChange({ ...filters, valueRange: "" }),
      });
    }
    if (filters.dateFrom || filters.dateTo) {
      const quick = quickDateValue ? quickLabelByValue[quickDateValue] : undefined;
      const label = quick
        ? `Período: ${quick}`
        : `Período: ${filters.dateFrom ? format(filters.dateFrom, "dd/MM/yyyy", { locale: ptBR }) : "-"} a ${filters.dateTo ? format(filters.dateTo, "dd/MM/yyyy", { locale: ptBR }) : "-"}`;
      chips.push({
        id: `period`,
        label,
        onClear: () => handleFiltersChange({ ...filters, dateFrom: undefined, dateTo: undefined }),
      });
    }
    if (filters.deliveryDateFrom || filters.deliveryDateTo) {
      const label = `Previsão: ${filters.deliveryDateFrom ? format(filters.deliveryDateFrom, "dd/MM/yyyy", { locale: ptBR }) : "-"} a ${filters.deliveryDateTo ? format(filters.deliveryDateTo, "dd/MM/yyyy", { locale: ptBR }) : "-"}`;
      chips.push({
        id: `delivery`,
        label,
        onClear: () => handleFiltersChange({ ...filters, deliveryDateFrom: undefined, deliveryDateTo: undefined }),
      });
    }
    return chips;
  }, [filters, quickDateValue, quickLabelByValue, clientNameById, supplierNameById]);

  const filteredTransactions = useMemo(() => {
    if (!transactions) return [] as Transaction[];
    return transactions.filter((transaction) => matchesFilters(transaction));
  }, [transactions, filters]);

  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredTransactions.slice(startIndex, endIndex);
  }, [filteredTransactions, currentPage, itemsPerPage]);

  // Calcular total de entradas usando valor líquido recebido (net_amount) dos pagamentos confirmados
  const totalEntradas = useMemo(() => {
    return filteredTransactions
      ?.filter((t) => t.tipo === "entrada" && t.status === "paga")
      .reduce((sum, t) => {
        const payments = paymentsByTx[t.id] || [];
        const valorLiquidoRecebido = payments
          .filter((p) => p.status === "confirmado")
          .reduce((paymentSum, p) => {
            const net = typeof p.net_amount === "number" ? Number(p.net_amount) : Number(p.valor) || 0;
            return paymentSum + net;
          }, 0);
        // Se não houver pagamentos confirmados, usar o valor líquido da transação como fallback
        return sum + (valorLiquidoRecebido > 0 ? valorLiquidoRecebido : Number(t.valor_liquido) || 0);
      }, 0) || 0;
  }, [filteredTransactions, paymentsByTx]);

  const totalSaidas = filteredTransactions
    ?.filter((t) => t.tipo === "saida" && t.status === "paga")
    .reduce((sum, t) => sum + Number(t.valor_liquido), 0) || 0;

  const saldoConsolidado = totalEntradas - totalSaidas;

  // Para entradas pendentes, usar o valor líquido da transação (ainda não recebido)
  const totalEntradasPendentes = filteredTransactions
    ?.filter((t) => t.tipo === "entrada" && t.status === "pendente")
    .reduce((sum, t) => sum + Number(t.valor_liquido), 0) || 0;

  const totalSaidasPendentes = filteredTransactions
    ?.filter((t) => t.tipo === "saida" && t.status === "pendente")
    .reduce((sum, t) => sum + Number(t.valor_liquido), 0) || 0;

  const saldoPrevisto = saldoConsolidado + totalEntradasPendentes - totalSaidasPendentes;

  // IDs de métodos presentes nas entradas
  const salesMethodIds = React.useMemo(() => {
    const ids = new Set<string>();
    filteredTransactions?.filter(t => t.tipo === "entrada").forEach(t => {
      (paymentsByTx[t.id] || []).forEach((p) => {
        if (p.payment_method_id) ids.add(String(p.payment_method_id));
      });
    });
    return Array.from(ids).sort();
  }, [filteredTransactions, paymentsByTx]);

  const expensePaymentMethods = React.useMemo(() => {
    const methods = new Set<string>();
    filteredTransactions?.filter(t => t.tipo === "saida").forEach(t => {
      const id = t.payment_method_id || t.pagamento_json?.metodo_pagamento;
      if (id) methods.add(String(id));
    });
    return Array.from(methods).sort();
  }, [filteredTransactions]);

  const expenseStatuses = React.useMemo(() => {
    const statuses = new Set<string>();
    filteredTransactions?.filter(t => t.tipo === "saida").forEach(t => {
      if (t.status) statuses.add(t.status);
    });
    return Array.from(statuses).sort();
  }, [filteredTransactions]);

  const salesMatrix = React.useMemo(() => {
    const statuses = ["pendente", "confirmado"] as const;
    const matrix: Record<string, Record<string, number>> = {};

    // Inicializa (chave por id)
    ["todos", ...salesMethodIds].forEach((id) => {
      matrix[id] = { todos: 0 } as Record<string, number>;
      statuses.forEach((s) => (matrix[id][s] = 0));
    });

    // Preenche usando valor líquido (net_amount) dos pagamentos confirmados
    filteredTransactions?.filter(t => t.tipo === "entrada").forEach(t => {
      (paymentsByTx[t.id] || []).forEach((p) => {
        const id = String(p.payment_method_id || "");
        // Para pagamentos confirmados, usar net_amount; para pendentes, usar valor
        const valor = p.status === "confirmado" 
          ? (typeof p.net_amount === "number" ? Number(p.net_amount) : Number(p.valor) || 0)
          : Number(p.valor || 0);
        const status = (p.status || "pendente") as "pendente" | "confirmado";

        matrix["todos"]["todos"] += valor;
        matrix["todos"][status] += valor;

        if (id && matrix[id]) {
          matrix[id]["todos"] += valor;
          matrix[id][status] += valor;
        }
      });
    });

    return matrix;
  }, [filteredTransactions, salesMethodIds, paymentsByTx]);

  const expensesMatrix = React.useMemo(() => {
    const matrix: Record<string, Record<string, number>> = {};

    // Inicializar matriz por ID de método
    ["todos", ...expensePaymentMethods].forEach((methodId) => {
      matrix[methodId] = { todos: 0 };
      expenseStatuses.forEach((status) => {
        matrix[methodId][status] = 0;
      });
    });

    // Preencher matriz
    filteredTransactions?.filter((t) => t.tipo === "saida").forEach((t) => {
      const methodId = String(t.payment_method_id || t.pagamento_json?.metodo_pagamento || "");
      const valor = Number(t.valor_liquido || 0);
      const status = t.status || "pendente";

      // Total geral
      matrix["todos"]["todos"] += valor;
      if (matrix["todos"][status] !== undefined) {
        matrix["todos"][status] += valor;
      }

      // Por método
      if (methodId && matrix[methodId]) {
        matrix[methodId]["todos"] += valor;
        if (matrix[methodId][status] !== undefined) {
          matrix[methodId][status] += valor;
        }
      }
    });

    return matrix;
  }, [filteredTransactions, expensePaymentMethods, expenseStatuses]);

  const allStatuses = useMemo(() => {
    const statuses = new Set<string>();
    transactions?.forEach((t) => {
      if (t.status) statuses.add(t.status);
    });
    return Array.from(statuses).sort();
  }, [transactions]);

  const allCategories = useMemo(() => {
    const categoryIds = new Set<string>();
    transactions?.forEach((t) => {
      // Apenas transações de saída têm expense_category_id
      if (t.tipo === "saida") {
        const expenseCategoryId = (t as any).expense_category_id;
        if (expenseCategoryId) categoryIds.add(expenseCategoryId);
      }
    });
    return Array.from(categoryIds).sort();
  }, [transactions]);

  return (
    <div className="space-y-4 md:space-y-6" data-cy="caixa-page">
      <Tabs defaultValue="transacoes">
        <TabsList className="mb-2">
          <TabsTrigger value="transacoes">Extrato de Entradas e Saídas</TabsTrigger>
          <TabsTrigger value="sessoes">Abertura e Fechamento de Caixa</TabsTrigger>
        </TabsList>
        <TabsContent value="transacoes">
          <div className="grid gap-2 md:gap-3 md:grid-cols-4 md:items-start md:justify-between">
            <div className="md:col-span-1">
              <h1 className="text-2xl md:text-3xl font-bold" data-cy="caixa-title">Caixa</h1>
              <p className="text-sm md:text-base text-muted-foreground">Gestão de transações e sessões</p>
            </div>
            <div className="md:col-span-3 flex flex-col items-stretch md:items-end gap-1">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative w-full sm:w-[150px]">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFiltersChange({ ...filters, searchTerm: e.target.value })}
                    className="pl-8"
                    data-cy="search-input"
                  />
                </div>
                <TransactionFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  clients={clients || []}
                  suppliers={suppliers || []}
                  bankAccounts={(bankAccountsRef || []).map(a => ({ id: a.id, nome: a.nome }))}
                  paymentMethods={allPaymentMethods}
                  paymentMethodNames={paymentMethodNameById}
                  categories={allCategories}
                  categoryNames={expenseCategoryNameById}
                  statuses={allStatuses}
                  products={allProducts}
                  productNames={productNameById}
                  counts={facetCounts}
                  data-cy="transaction-filters"
                />
                <Select value={quickDateValue} onValueChange={handleQuickDateChange}>
                  <SelectTrigger className="w-full sm:w-[140px]" data-cy="quick-date-select">
                    <SelectValue placeholder="Período rápido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clear">Limpar período</SelectItem>
                    <SelectSeparator />
                    {QUICK_DATE_PRESETS.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value}>
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  className="bg-primary hover:bg-primary/90"
                  onClick={() => {
                    if (openSessionShortcut) {
                      setShowCloseSession(true);
                    } else {
                      setShowOpenSession(true);
                    }
                  }}
                >
                  {openSessionShortcut ? "Fechar Caixa" : "Abrir Caixa"}
                </Button>
                <div className="flex gap-2">
                  <Button onClick={() => {
                    saleModalOpenedByUser.current = true;
                    setShowNewSale(true);
                  }} className="flex-1 sm:flex-none bg-success hover:bg-success/90 bg-lime-700 hover:bg-lime-600" data-cy="new-sale-btn">
                    <ArrowUpCircle className="h-4 w-4" />
                    <span className="ml-1">Venda</span>
                  </Button>
                  <Button onClick={() => {
                    expenseModalOpenedByUser.current = true;
                    setShowNewExpense(true);
                    // Atualizar URL com parâmetro openExpense apenas se não estiver presente
                    const searchParams = new URLSearchParams(location.search);
                    if (searchParams.get('openExpense') !== 'true') {
                      searchParams.set('openExpense', 'true');
                      const newSearch = `?${searchParams.toString()}`;
                      // Atualizar ref antes de navegar para evitar que o useEffect reaja
                      lastSearchRef.current = newSearch;
                      navigate(`${location.pathname}${newSearch}`, { replace: true });
                    }
                  }} variant="destructive" className="flex-1 sm:flex-none" data-cy="new-expense-btn">
                    <ArrowDownCircle className="h-4 w-4" />
                    <span className="ml-1">Saída</span>
                  </Button>
                </div>
              </div>
              {activeFilterChips.length > 0 && (
                <div className="text-[13px] md:text-sm text-muted-foreground text-right">
                  <span className="mr-2 font-bold">Filtros ativos:</span>
                  <div className="inline-flex flex-wrap gap-0.5 justify-end">
                    {activeFilterChips.map((chip) => (
                      <Badge key={chip.id} variant="secondary" className="h-5 px-1.5 py-0 inline-flex items-center gap-1 border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200">
                        <span>{chip.label}</span>
                        <button
                          type="button"
                          onClick={chip.onClear}
                          className="text-red-600 hover:bg-red-600 hover:text-white rounded-full w-4 h-4 inline-flex items-center justify-center leading-none"
                          aria-label={`Remover ${chip.label}`}
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

      <br />
      <div className="grid gap-4 md:grid-cols-2">
        <Card data-cy="sales-summary-card">
          <CardHeader className={showSummaryTables ? "pb-0 pt-3" : "pb-4 pt-3"}>
            <CardTitle 
              className="text-base md:text-xl text-green-600 flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity" 
              data-cy="sales-summary-title"
              onClick={() => setShowSummaryTables(!showSummaryTables)}
            >
              <span>Entradas | Totais de Vendas</span>
              {showSummaryTables ? (
                <ChevronUp className="h-6 w-6" />
              ) : (
                <ChevronDown className="h-6 w-6" />
              )}
            </CardTitle>
          </CardHeader>
          {showSummaryTables && (
          <CardContent className="px-3 pb-3 pt-0 md:px-6 md:pb-6 md:pt-0">
            <div className="overflow-x-auto -mx-3 md:mx-0">
              <Table data-cy="sales-matrix-table" className="text-xs md:text-sm">
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold">Método</TableHead>
                    <TableHead className="text-right font-semibold">Todos os Status</TableHead>
                    <TableHead className="text-right font-semibold">Pendente</TableHead>
                    <TableHead className="text-right font-semibold">Confirmado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {["todos", ...salesMethodIds].map(methodId => (
                    <TableRow key={methodId} data-cy={`sales-row-${methodId}`}>
                      <TableCell className="font-medium capitalize px-2.5 py-1.5">
                        {methodId === "todos" ? "Todos os Métodos" : (paymentMethodNameById[methodId] || methodId)}
                      </TableCell>
                      <TableCell className="text-right px-2.5 py-1.5" data-cy={`sales-total-${methodId}`}>
                        <MoneyValue
                          asBlock
                          value={salesMatrix[methodId]?.todos || 0}
                          valueClassName="font-medium"
                          className="justify-end"
                        />
                      </TableCell>
                      <TableCell className="text-right text-warning px-2.5 py-1.5" data-cy={`sales-pending-${methodId}`}>
                        <MoneyValue
                          asBlock
                          value={salesMatrix[methodId]?.pendente || 0}
                          valueClassName="font-medium text-warning"
                          className="justify-end"
                        />
                      </TableCell>
                      <TableCell className="text-right text-success px-2.5 py-1.5" data-cy={`sales-confirmed-${methodId}`}>
                        <MoneyValue
                          asBlock
                          value={salesMatrix[methodId]?.confirmado || 0}
                          valueClassName="font-medium text-success"
                          className="justify-end"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          )}
        </Card>

        <Card data-cy="expenses-summary-card">
          <CardHeader className={showSummaryTables ? "pb-0 pt-3" : "pb-4 pt-3"}>
            <CardTitle 
              className="text-base md:text-xl text-red-600 flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity" 
              data-cy="expenses-summary-title"
              onClick={() => setShowSummaryTables(!showSummaryTables)}
            >
              <span>Saídas | Total de Despesas</span>
              {showSummaryTables ? (
                <ChevronUp className="h-6 w-6" />
              ) : (
                <ChevronDown className="h-6 w-6" />
              )}
            </CardTitle>
          </CardHeader>
          {showSummaryTables && (
          <CardContent className="px-3 pb-3 pt-0 md:px-6 md:pb-6 md:pt-0">
            <div className="overflow-x-auto -mx-3 md:mx-0">
              <Table data-cy="expenses-matrix-table" className="text-xs md:text-sm">
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold">Métodos/Status</TableHead>
                    <TableHead className="text-right font-semibold">Todos os Status</TableHead>
                    {expenseStatuses.map(status => (
                      <TableHead key={status} className="text-right font-semibold capitalize">
                        {status}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {["todos", ...expensePaymentMethods].map(methodId => (
                    <TableRow key={methodId} data-cy={`expenses-row-${methodId}`}>
                      <TableCell className="font-medium capitalize px-2.5 py-1.5">
                        {methodId === "todos" ? "Todos os Métodos" : (paymentMethodNameById[methodId] || methodId)}
                      </TableCell>
                      <TableCell className="text-right px-2.5 py-1.5" data-cy={`expenses-total-${methodId}`}>
                        <MoneyValue
                          asBlock
                          value={expensesMatrix[methodId]?.todos || 0}
                          valueClassName="font-medium"
                          className="justify-end"
                        />
                      </TableCell>
                      {expenseStatuses.map(status => (
                        <TableCell key={status} className="text-right text-destructive px-2.5 py-1.5" data-cy={`expenses-${methodId}-${status}`}>
                          <MoneyValue
                            asBlock
                            value={expensesMatrix[methodId]?.[status] || 0}
                            valueClassName="font-medium text-destructive"
                            className="justify-end"
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          )}
        </Card>
      </div>

      <br />
      <Card data-cy="transactions-card">
        <CardHeader className="mb-0 pb-0">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-base md:text-xl" data-cy="transactions-title">Extrato de Transações</CardTitle>
              <button
                type="button"
                onClick={toggleShowMoneyValues}
                className="text-x mt-1 sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
                aria-pressed={showMoneyValues}
              >
                {showMoneyValues ? "Ocultar Valores" : "Mostrar Valores"}
              </button>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="text-left sm:text-right" data-cy="saldo-consolidado">
                <p className="text-xs text-muted-foreground">Saldo Consolidado</p>
                <MoneyValue
                  value={saldoConsolidado}
                  valueClassName={`text-base md:text-lg font-bold ${saldoConsolidado >= 0 ? 'text-success' : 'text-destructive'}`}
                  className="justify-end"
                  data-cy="saldo-consolidado-value"
                />
              </div>
              <div className="text-left sm:text-right" data-cy="saldo-previsto">
                <p className="text-xs text-muted-foreground">Saldo Previsto</p>
                <MoneyValue
                  value={saldoPrevisto}
                  valueClassName={`text-base md:text-lg font-bold ${saldoPrevisto >= 0 ? 'text-success' : 'text-destructive'}`}
                  className="justify-end"
                  data-cy="saldo-previsto-value"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 -mt-2 md:p-6">
          {isLoading ? (
            <div className="text-center py-8" data-cy="transactions-loading">Carregando...</div>
          ) : paginatedTransactions && paginatedTransactions.length > 0 ? (
            <>
              <div className="space-y-3 md:hidden">
                {paginatedTransactions.map((transaction) => {
                  const client = transaction.client_id ? clients?.find((c) => c.id === transaction.client_id) : null;
                  const supplier = transaction.supplier_id ? suppliers?.find((s) => s.id === transaction.supplier_id) : null;
                  const items = transactionItems?.filter((item) => item.transaction_id === transaction.id) || [];

                  return (
                    <div
                      key={transaction.id}
                      className="rounded-lg border bg-card shadow-sm transition hover:shadow-md"
                      data-cy={`transaction-card-${transaction.id}`}
                    >
                      <div className="flex flex-col gap-3 px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {renderTypeBadge(transaction)}
                            <span>{format(new Date(transaction.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}</span>
                          </div>
                          <div className="font-semibold text-sm text-muted-foreground">
                            {transaction.tipo === "entrada" ? "Cliente" : "Fornecedor"}
                          </div>
                          <div className="text-base font-semibold text-foreground" data-cy={`transaction-entity-${transaction.id}`}>
                            {transaction.tipo === "entrada" ? (
                              client?.id ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setClientProfile({ id: client.id, name: client?.nome || '-' });
                                        }}
                                        className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer"
                                      >
                                        <span>{client?.nome || "-"}</span>
                                        <FileText className="h-4 w-4 text-muted-foreground" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent className="whitespace-pre-line">
                                      Ver ficha completa e relatório do cliente{'\n'}<strong>{client?.nome || 'cliente'}</strong>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                client?.nome || "-"
                              )
                            ) : (
                              supplier?.id ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSupplierProfile({ id: supplier.id, name: supplier?.nome || '-' });
                                        }}
                                        className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                      >
                                        <span>{supplier?.nome || "-"}</span>
                                        <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent className="whitespace-pre-line">
                                      Ver ficha completa e relatório do fornecedor{'\n'}<strong>{supplier?.nome || 'fornecedor'}</strong>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                supplier?.nome || "-"
                              )
                            )}
                          </div>
                          {(client?.telefone || supplier?.telefone) && (
                            <div className="text-xs text-muted-foreground">
                              {transaction.tipo === "entrada" ? formatPhone(client?.telefone || "") : formatPhone(supplier?.telefone || "")}
                            </div>
                          )}
                        </div>

                        <div className="grid gap-2 text-sm text-muted-foreground">
                          <div>
                            <span className="font-medium text-foreground">Categoria/Produto:</span>
                            {transaction.tipo === "entrada" ? (
                              items.length > 0 ? (
                                <div className="mt-1 space-y-1">
                                  {items.map((item: any) => (
                                    <div key={item.id} className="flex items-center gap-1">
                                      {item.product_id ? (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setProductProfile({ id: item.product_id, name: item.products?.nome || 'produto' });
                                                }}
                                                className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                              >
                                                <span>{truncateProductName(item.products?.nome)}</span>
                                                <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                              </button>
                                            </TooltipTrigger>
                                            <TooltipContent className="whitespace-pre-line">
                                              Ver ficha completa e relatórios{'\n'}do produto <strong>{item.products?.nome || 'produto'}</strong>{'\n'}Quantidade: {item.quantidade || 0}{(() => {
                                                if (!unitMeasurements || unitMeasurements.length === 0) return null;
                                                const saleUnitId = item.products?.unit_measurement_sale_id;
                                                if (!saleUnitId) return null;
                                                const saleUnit = unitMeasurements.find(u => u.id === saleUnitId);
                                                return saleUnit ? <span className="text-muted-foreground"> {saleUnit.slug}</span> : null;
                                              })()} • Valor: {formatCurrency(item.total || 0)}{'\n'}Valor Total: <strong>{formatCurrency(item.total || 0)}</strong>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      ) : (
                                        <span>{truncateProductName(item.products?.nome)}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="ml-1">-</span>
                              )
                            ) : (
                              <div className="mt-1 space-y-1">
                                {(() => {
                                  const expenseCategoryId = (transaction as any).expense_category_id;
                                  const categoryName = expenseCategoryId ? expenseCategoryNameById[expenseCategoryId] : null;
                                  return categoryName ? (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (expenseCategoryId) {
                                                setExpenseCategoryProfile({ id: expenseCategoryId, name: categoryName });
                                              }
                                            }}
                                            className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                          >
                                            <span>{categoryName}</span>
                                            <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent className="whitespace-pre-line">
                                          Ver ficha completa e relatórios{'\n'}da categoria <strong>{categoryName}</strong>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : (
                                    <div>-</div>
                                  );
                                })()}
                                {transaction.descricao && <div className="text-xs">{transaction.descricao}</div>}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2" data-cy={`transaction-status-${transaction.id}`}>
                            <span className="font-medium text-foreground">Status:</span>
                            {renderStatusBadge(transaction.status)}
                          </div>
                          <div data-cy={`transaction-value-${transaction.id}`}>
                            <span className="font-medium text-foreground">Valor:</span>
                            <div className="mt-1">
                              {renderTransactionValue(transaction, "start")}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 border-t px-4 py-3 sm:flex-row">
                        <div className="flex w-full gap-2">
                          {transaction.tipo === "entrada" && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => setPrintPreviewTransaction(transaction)}
                                  >
                                    <Printer className="mr-2 h-4 w-4" />
                                    Imprimir
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Visualizar para impressão
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {isLockedByClosedSession(transaction) ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 icon-chip-blue"
                              onClick={() => {
                                setViewOnlyMode(true);
                                handleEdit(transaction);
                              }}
                              title="Visualizar transação (sessão fechada)"
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Detalhes
                            </Button>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 icon-chip-green"
                                    onClick={() => handleEdit(transaction)}
                                    data-cy={`transaction-edit-${transaction.id}`}
                                  >
                                    <Pencil className="mr-2 h-4 w-4" />
                                    Editar
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent className="whitespace-pre-line">
                                  {transaction.tipo === "entrada" 
                                    ? <>Abrir venda do cliente{'\n'}<strong>{client?.nome || 'cliente'}</strong></>
                                    : <>Editar esta despesa do cliente{'\n'}<strong>{client?.nome || 'cliente'}</strong></>}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {isLockedByClosedSession(transaction) ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="flex-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full icon-chip-red"
                                    disabled
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Excluir
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                Essa transação já foi conferida e está em uma sessão de caixa fechado
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 icon-chip-red"
                                    onClick={() => setDeletingId(transaction.id)}
                                    data-cy={`transaction-delete-${transaction.id}`}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Excluir
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent className="whitespace-pre-line">
                                  {transaction.tipo === "entrada" 
                                    ? <>Excluir venda do cliente{'\n'}<strong>{client?.nome || 'cliente'}</strong></>
                                    : <>Excluir despesa do cliente{'\n'}<strong>{client?.nome || 'cliente'}</strong></>}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="hidden md:block rounded-md border overflow-x-auto">
                <Table data-cy="transactions-table" className="text-xs md:text-sm">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Tipo/Data</TableHead>
                      <TableHead className="whitespace-nowrap">Cliente/Fornecedor</TableHead>
                      <TableHead className="whitespace-nowrap">Categoria/Produto</TableHead>
                      <TableHead className="text-center whitespace-nowrap">Status</TableHead>
                      <TableHead className="text-center whitespace-nowrap">Valor</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTransactions.map((transaction) => {
                      const client = transaction.client_id ? clients?.find((c) => c.id === transaction.client_id) : null;
                      const supplier = transaction.supplier_id ? suppliers?.find((s) => s.id === transaction.supplier_id) : null;
                      const items = transactionItems?.filter((item) => item.transaction_id === transaction.id) || [];

                      return (
                        <TableRow key={transaction.id} data-cy={`transaction-row-${transaction.id}`}>
                          <TableCell data-cy={`transaction-date-${transaction.id}`}>
                            <div className="flex flex-col gap-1 items-start">
                              {renderTypeBadge(transaction)}
                              <div>{format(new Date(transaction.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {transaction.tipo === "entrada" ? (
                              <div>
                                {client?.id ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setClientProfile({ id: client.id, name: client?.nome || '-' });
                                          }}
                                          className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                        >
                                          <span>{client?.nome || "-"}</span>
                                          <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent className="whitespace-pre-line">
                                        Ver ficha completa e relatório do cliente{'\n'}<strong>{client?.nome || 'cliente'}</strong>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <div>{client?.nome || "-"}</div>
                                )}
                                {client?.telefone && (
                                  <div className="text-xs text-muted-foreground">{formatPhone(client.telefone)}</div>
                                )}
                              </div>
                            ) : (
                              <div>
                                {supplier?.id ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSupplierProfile({ id: supplier.id, name: supplier?.nome || '-' });
                                          }}
                                          className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                        >
                                          <span>{supplier?.nome || "-"}</span>
                                          <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent className="whitespace-pre-line">
                                        Ver ficha completa e relatório do fornecedor{'\n'}<strong>{supplier?.nome || 'fornecedor'}</strong>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <div>{supplier?.nome || "-"}</div>
                                )}
                                {supplier?.telefone && (
                                  <div className="text-xs text-muted-foreground">{formatPhone(supplier.telefone)}</div>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell data-cy={`transaction-category-${transaction.id}`}>
                            {transaction.tipo === "entrada" ? (
                              items.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                  {items.map((item: any) => (
                                    <div key={item.id} className="text-sm flex items-center gap-1">
                                      {item.product_id ? (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setProductProfile({ id: item.product_id, name: item.products?.nome || 'produto' });
                                                }}
                                                className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                              >
                                                <span>{truncateProductName(item.products?.nome)}</span>
                                                <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                              </button>
                                            </TooltipTrigger>
                                            <TooltipContent className="whitespace-pre-line">
                                              Ver ficha completa e relatórios{'\n'}do produto <strong>{item.products?.nome || 'produto'}</strong>{'\n'}Quantidade: {item.quantidade || 0}{(() => {
                                                if (!unitMeasurements || unitMeasurements.length === 0) return null;
                                                const saleUnitId = item.products?.unit_measurement_sale_id;
                                                if (!saleUnitId) return null;
                                                const saleUnit = unitMeasurements.find(u => u.id === saleUnitId);
                                                return saleUnit ? <span className="text-muted-foreground"> {saleUnit.slug}</span> : null;
                                              })()} • Valor: {formatCurrency(item.total || 0)}{'\n'}Valor Total: <strong>{formatCurrency(item.total || 0)}</strong>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      ) : (
                                        <span>{truncateProductName(item.products?.nome)}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                "-"
                              )
                            ) : (
                              <div>
                                {(() => {
                                  const expenseCategoryId = (transaction as any).expense_category_id;
                                  const categoryName = expenseCategoryId ? expenseCategoryNameById[expenseCategoryId] : null;
                                  return categoryName ? (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (expenseCategoryId) {
                                                setExpenseCategoryProfile({ id: expenseCategoryId, name: categoryName });
                                              }
                                            }}
                                            className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                          >
                                            <span>{categoryName}</span>
                                            <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent className="whitespace-pre-line">
                                          Ver ficha completa e relatórios{'\n'}da categoria <strong>{categoryName}</strong>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : (
                                    <div>-</div>
                                  );
                                })()}
                                {transaction.descricao && (
                                  <div className="text-xs text-muted-foreground">{transaction.descricao}</div>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-center" data-cy={`transaction-status-desktop-${transaction.id}`}>
                            {renderStatusBadge(transaction.status)}
                          </TableCell>
                          <TableCell className="text-left" data-cy={`transaction-value-desktop-${transaction.id}`}>
                            {renderTransactionValue(transaction, "start")}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <div className="flex gap-2 justify-end">
                              {transaction.tipo === "entrada" && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setPrintPreviewTransaction(transaction)}
                                        title="Visualizar para impressão"
                                      >
                                        <Printer className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Visualizar para impressão
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {isLockedByClosedSession(transaction) ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="icon-chip-blue"
                                  onClick={() => {
                                    setViewOnlyMode(true);
                                    handleEdit(transaction);
                                  }}
                                  title="Visualizar transação (sessão fechada)"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              ) : (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="icon-chip-green"
                                        onClick={() => handleEdit(transaction)}
                                        data-cy={`transaction-edit-desktop-${transaction.id}`}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent className="whitespace-pre-line">
                                      {transaction.tipo === "entrada" 
                                        ? <>Abrir venda do cliente{'\n'}<strong>{client?.nome || 'cliente'}</strong></>
                                        : <>Editar esta despesa do cliente{'\n'}<strong>{client?.nome || 'cliente'}</strong></>}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {isLockedByClosedSession(transaction) ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="icon-chip-red"
                                        disabled
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Essa transação já foi conferida e está em uma sessão de caixa fechado
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setDeletingId(transaction.id)}
                                        data-cy={`transaction-delete-desktop-${transaction.id}`}
                                        className="icon-chip-red"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent className="whitespace-pre-line">
                                      {transaction.tipo === "entrada" 
                                        ? <>Excluir venda do cliente{'\n'}<strong>{client?.nome || 'cliente'}</strong></>
                                        : <>Excluir despesa do cliente{'\n'}<strong>{client?.nome || 'cliente'}</strong></>}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 px-4 md:px-0">
                <TablePagination
                  currentPage={currentPage}
                  totalPages={Math.ceil((filteredTransactions?.length || 0) / itemsPerPage)}
                  totalItems={filteredTransactions?.length || 0}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                  data-cy="transactions-pagination"
                />
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground" data-cy="transactions-empty">
              Nenhuma transação registrada.
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="sessoes">
          <CashRegisterSessions />
        </TabsContent>
      </Tabs>

      <NewSaleModal open={showNewSale} onOpenChange={handleCloseModal} transaction={editingTransaction} readOnly={viewOnlyMode} />
      <NewExpenseModal open={showNewExpense} onOpenChange={(open) => handleCloseModal(open, 'expense')} transaction={editingTransaction} readOnly={viewOnlyMode} />
      <OpenSessionModal open={showOpenSession} onOpenChange={setShowOpenSession} />
      {openSessionShortcut && (
        <CloseSessionModal
          open={showCloseSession}
          onOpenChange={setShowCloseSession}
          session={openSessionShortcut}
        />
      )}
      {printPreviewTransaction && (
        <PrintPreviewModal
          open={!!printPreviewTransaction}
          onOpenChange={(open) => {
            if (!open) setPrintPreviewTransaction(null);
          }}
          transaction={printPreviewTransaction}
          items={transactionItems?.filter((item) => item.transaction_id === printPreviewTransaction.id) || []}
          payments={paymentsByTx[printPreviewTransaction.id] || []}
          client={printPreviewTransaction.client_id ? clients?.find((c) => c.id === printPreviewTransaction.client_id) || null : null}
          seller={printPreviewTransaction.vendedor_id ? sellers?.find((s) => s.id === printPreviewTransaction.vendedor_id) || null : null}
          tenant={{ name: tenantName || "", email: null }}
        />
      )}

      <AlertDialog
        open={!!deletingId}
        onOpenChange={() => {
          console.log("🔒 Fechando dialog de exclusão");
          setDeletingId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta transação? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                console.log("🗑️ Botão excluir clicado, ID:", deletingId);
                if (deletingId) {
                  deleteMutation.mutate(deletingId);
                }
              }}
              className="bg-destructive hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {clientProfile && (() => {
        const c = (clients || []).find((x: any) => x.id === clientProfile?.id);
        const photo = c?.foto_url || null;
        const infoItems = [
          c?.created_at ? { value: `Cliente desde ${new Intl.DateTimeFormat('pt-BR').format(new Date(c.created_at))}` } : null,
          c?.telefone ? { value: formatPhone(c.telefone as string) } : null,
          c?.email ? { value: c.email } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!clientProfile}
            onOpenChange={() => setClientProfile(null)}
            imageUrl={photo || null}
            entityTypeLabel={"CLIENTE"}
            entityName={clientProfile.name}
            infoItems={infoItems}
            renderContent={({ periodRange, setLoading }) => (
              <ClientDetails id={clientProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}

      {supplierProfile && (() => {
        const s = (suppliers || []).find((x: any) => x.id === supplierProfile?.id);
        const photo = s?.foto_url || null;
        const infoItems = [
          s?.email ? { value: s.email } : null,
          s?.telefone ? { value: formatPhone(s.telefone) } : null,
          s?.endereco_completo ? { value: s.endereco_completo, className: "truncate max-w-[28rem]" } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!supplierProfile}
            onOpenChange={() => setSupplierProfile(null)}
            imageUrl={photo}
            entityTypeLabel={"FORNECEDOR"}
            entityName={s?.nome || "Fornecedor"}
            infoItems={infoItems}
            renderContent={({ periodRange, setLoading }) => (
              <SupplierDetails id={supplierProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}

      {productProfile && (() => {
        return (
          <RaioXModal
            open={!!productProfile}
            onOpenChange={() => setProductProfile(null)}
            imageUrl={null}
            entityTypeLabel={"PRODUTO"}
            entityName={productProfile.name || "Produto"}
            infoItems={[]}
            renderContent={({ periodRange, setLoading }) => (
              productProfile ? <ProductDetailsView id={productProfile.id} embedded periodRange={periodRange} setLoading={setLoading} /> : null
            )}
          />
        );
      })()}

      {expenseCategoryProfile && (() => {
        const cat = (expenseCategories || []).find((x: any) => x.id === expenseCategoryProfile?.id);
        const infoItems = [
          (cat as any)?.descricao ? { value: (cat as any).descricao } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!expenseCategoryProfile}
            onOpenChange={() => setExpenseCategoryProfile(null)}
            imageUrl={null}
            entityTypeLabel={"CATEGORIA DE DESPESA"}
            entityName={(cat as any)?.nome || "Categoria"}
            infoItems={infoItems}
            renderContent={({ periodRange, setLoading }) => (
              <ExpenseCategoryDetails id={expenseCategoryProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}

      {productCategoryProfile && (() => {
        const cat = (productCategories || []).find((c: any) => c.id === productCategoryProfile.id);
        return (
          <RaioXModal
            open={!!productCategoryProfile}
            onOpenChange={() => setProductCategoryProfile(null)}
            imageUrl={null}
            entityTypeLabel={"CATEGORIA DE PRODUTO"}
            entityName={cat?.nome || productCategoryProfile.name || "Categoria"}
            infoItems={[]}
            renderContent={({ periodRange, setLoading }) => (
              <ProductCategoryDetails id={productCategoryProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}

      {supplierProfile && (() => {
        const s = (suppliers || []).find((x: any) => x.id === supplierProfile?.id);
        const photo = s?.foto_url || null;
        const infoItems = [
          s?.created_at ? { value: `Fornecedor desde ${new Intl.DateTimeFormat('pt-BR').format(new Date(s.created_at))}` } : null,
          s?.telefone ? { value: formatPhone(s.telefone as string) } : null,
          s?.email ? { value: s.email } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!supplierProfile}
            onOpenChange={() => setSupplierProfile(null)}
            imageUrl={photo || null}
            entityTypeLabel={"FORNECEDOR"}
            entityName={supplierProfile.name}
            infoItems={infoItems}
            renderContent={({ periodRange, setLoading }) => (
              <SupplierDetails id={supplierProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}

      {productProfile && (
        <RaioXModal
          open={!!productProfile}
          onOpenChange={() => setProductProfile(null)}
          imageUrl={productHeader?.foto_url || null}
          entityTypeLabel="Produto"
          entityName={productHeader?.nome || productProfile.name}
          infoItems={[
            productHeader?.codigo_sku ? { value: `SKU: ${productHeader.codigo_sku}` } : null,
            productHeader?.product_categories?.nome
              ? { value: productHeader.product_categories.nome, badge: true, onClick: () => setProductCategoryProfile({ id: productHeader.product_category_id, name: productHeader.product_categories.nome }) }
              : null,
            productHeader?.created_at
              ? { value: `Produto desde ${new Intl.DateTimeFormat('pt-BR').format(new Date(productHeader.created_at))}` }
              : null,
          ].filter(Boolean) as any}
          showPeriodControls
          initialFrom={startOfDay(subDays(new Date(), 6))}
          initialTo={endOfDay(new Date())}
          onPeriodChange={(from, to) => { setRxFrom(from); setRxTo(to); }}
          renderContent={({ periodRange, setLoading }) => (
            productProfile ? (
              <ProductDetailsView id={productProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            ) : null
          )}
        />
      )}

      {expenseCategoryProfile && (() => {
        const cat = (expenseCategories || []).find((x: any) => x.id === expenseCategoryProfile?.id);
        const infoItems = [
          (cat as any)?.descricao ? { value: (cat as any).descricao } : null,
          (cat as any)?.created_at ? { value: `Criada em ${new Intl.DateTimeFormat('pt-BR').format(new Date((cat as any).created_at))}` } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!expenseCategoryProfile}
            onOpenChange={() => setExpenseCategoryProfile(null)}
            imageUrl={null}
            entityTypeLabel={"CATEGORIA DE DESPESA"}
            entityName={expenseCategoryProfile.name}
            infoItems={infoItems}
            contentClassName="px-2 pb-2 pt-1"
            renderContent={({ periodRange, setLoading }) => (
              <ExpenseCategoryDetails id={expenseCategoryProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}

    </div>
  );
}
